/* eslint-disable no-param-reassign */

import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import apiService from '@services/apiService';
import {
  addNewClientPhoto,
  addFlexRulePhoto,
  addEditPhotoResult,
  addApiUploadResult,
  softDeletePhoto,
  clearPhotoCollection,
  removeEditPhoto,
  restorePhotoMetaByKey,
} from '@redux/modules/photo';

import {
  selectPhotoMetaCollection,
  selectPhotosToUpload,
  selectPhotoToEdit,
  selectMaxSortOrder,
} from '@redux/selectors/PhotoSelector';
import photoPlaceholder from '@images/image-not-available.png';
import API from '../config';

const usePhotoManager = () => {
  const dispatch = useDispatch();

  // retrieves entire collection
  const photoMetaCollection = useSelector(selectPhotoMetaCollection);

  // retrieve only photos without externalURL
  const photosToUpload = useSelector(selectPhotosToUpload);

  const editPhotoMeta = useSelector(selectPhotoToEdit);
  const maxSort = useSelector(selectMaxSortOrder);
  // tracks by key which photos have been passed to the api already.
  const [photoUploadStatus, setPhotoUploadStatus] = useState([]);

  const getPhotoMetaObject = () => ({
    key: null,
    id: null,
    originalURL: null,
    srcURL: null,
    cropperSrcURL: null,
    externalURL: null,
    SortOrder: null,
    IsDeleted: null,
    IsUploaded: false,
    IsError: false,
    Description: null,
    thumbailSrcURL: null,
    thumbExternalURL: null,
    cropPointer: null,
    cropperResult: {
      file: null,
      blobURL: null,
    },
    dropZoneMeta: {
      file: null,
      blobURL: null,
    },
    apiMeta: {
      assetType: null,
      displayName: null,
      extension: null,
      isError: null,
      newName: null,
      path: null,
      url: null,
    },
    formikMeta: {
      URL: null,
      SortOrder: null,
      IsDeleted: null,
      IsLive: null,
      IsPreload: null,
      Description: null,
      MediaType: null,
      MediaCategory: null,
    },
    flexMeta: {
      UniqueID: null,
      ParentID: null,
      SortOrder: null,
      IsDeleted: null,
      Description: null,
      MediaCategory: null,
      MediaKeyNumeric: null,
      MediaType: null,
      ModificationTimestamp: null,
      Order: null,
      OriginatingSystemMediaKey: null,
      OriginatingSystemName: null,
      PreferredPhotoYN: null,
      ResourceName: null,
      Path: null,
      URL: null,
      ResourceRecordKeyNumeric: null,
      ModifiedDate: null,
      IsLive: null,
      LongDescription: null,
      ParentType: null,
      OriginatingSystemParent: null,
      IsPreload: null,
      fullSizeURL: null,
      InitialSortOrder: null,
    },
  });

  const createThumbNailFile = photoMetaFilePtr => {
    let thumbnail;
    const fileName = photoMetaFilePtr.path ?? photoMetaFilePtr.name;
    const name = fileName.replace(/(\.[\w\d_-]+)$/i, '_thumbnail$1');
    thumbnail = new File([photoMetaFilePtr], name, {
      type: photoMetaFilePtr.type,
    });
    thumbnail = Object.assign(thumbnail, {
      length: photoMetaFilePtr.length,
    });
    return thumbnail;
  };

  const uuidRegexPattern = /[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}/i;
  const createThumbnailExternalLink = photoMeta => {
    const externalFullSizeUrl = photoMeta.externalURL;
    if (externalFullSizeUrl) {
      if (!externalFullSizeUrl.match(uuidRegexPattern)) return externalFullSizeUrl; // thumbnail image does not exist
      return externalFullSizeUrl.replace(/(\.[\w\d_-]+)$/i, '_thumbnail$1');
    }
    return '';
  };

  const postFormFiles = async (formFiles, photoMeta) => {
    /*
      +++++++ Base API return
      assetType: "image"
      photoMeta.apiMeta.displayName: "cat1_thumbnail.jpg"
      photoMeta.apiMeta.extension: ".jpg"
      photoMeta.apiMeta.isError: false
      photoMeta.apiMeta.newName: "https://dev-pillarnineonmarket.s3.ca-central-1.amazonaws.com/8f46d3eb63af4315b7bff500cceff7d8_thumbnail.jpg"
      photoMeta.apiMeta.path: "8f46d3eb63af4315b7bff500cceff7d8_thumbnail.jpg"
      photoMeta.apiMeta.url: "https://dev-pillarnineonmarket.s3.ca-central-1.amazonaws.com/8f46d3eb63af4315b7bff500cceff7d8_thumbnail.jpg"
     */
    try {
      const result = await apiService.Photos.postImage(formFiles);
      result.forEach(res => {
        if (res.path.includes('_thumbnail')) {
          photoMeta.thumbExternalURL = res.url;
          return;
        }
        photoMeta.IsUploaded = true;
        photoMeta.externalURL = res.url;
        photoMeta.apiMeta.assetType = res.assetType;
        photoMeta.apiMeta.displayName = res.displayName;
        photoMeta.apiMeta.extension = res.extension;
        photoMeta.apiMeta.isError = res.isError;
        photoMeta.apiMeta.newName = res.newName;
        photoMeta.apiMeta.path = res.path;
        photoMeta.apiMeta.url = res.url;
      });
      // write photoMeta back to redux.
      dispatch(addApiUploadResult(photoMeta));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error: usePhotoManager postFormFiles', error);
      photoMeta.IsError = true;
      if (photoMeta.cropPointer) {
        photoMeta.IsDeleted = true;
        dispatch(restorePhotoMetaByKey(photoMeta.cropPointer));
      } else {
        dispatch(addApiUploadResult(photoMeta));
      }
    }
  };

  const uploadPhoto = async photoMeta => {
    try {
      const metaFile = photoMeta.cropperResult.file ?? photoMeta.dropZoneMeta.file;
      const thumbnail = createThumbNailFile(metaFile);
      const formFiles = new FormData();
      formFiles.append('cropperImageList', metaFile);
      formFiles.append('cropperImageList', thumbnail);
      await postFormFiles(formFiles, photoMeta); // dispatch key w/ formfile to maintain object provenance
      formFiles.delete('cropperImageList');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  // set upload flags to track file uploads
  // this runs 4 times for 1 photo.
  useEffect(() => {
    if (photosToUpload && photosToUpload.length > 0) {
      photosToUpload.forEach(pm => {
        if (!photoUploadStatus.find(status => status.key === pm.key)) {
          const newStatus = {
            key: pm.key,
            toUpload: true,
            uploadComplete: false,
            reUpload: false,
          };
          setPhotoUploadStatus([...photoUploadStatus, newStatus]);
        }
      });
    }
  }, [photosToUpload, photoUploadStatus]);

  // upload photos based on status
  useEffect(() => {
    if (photoUploadStatus.length > 0) {
      const needToProcess = photoUploadStatus.filter(pU => pU.toUpload);
      if (needToProcess.length > 0) {
        needToProcess.forEach(async processKey => {
          const targetPM = photoMetaCollection.find(pm => pm.key === processKey.key);
          if (targetPM) await uploadPhoto(targetPM);
        });
        const processedStatuses = photoUploadStatus.map(pU => {
          const element = pU;
          const status = needToProcess.find(n => n.key === pU.key);
          if (!status) return element;
          element.toUpload = false;
          element.uploadComplete = true;
          return element;
        });
        setPhotoUploadStatus(processedStatuses);
      }
    }
  }, [photoUploadStatus]);

  useEffect(() => {
    if (photoMetaCollection.length === 0 && photoUploadStatus.length !== 0) setPhotoUploadStatus([]);
  }, [photoMetaCollection]);

  // index passed to handle bulk drop of photos, as only the dropzone will know how many files are coming in.
  const addNewDropzonePhoto = (photo, index) => {
    /* default file params.
        {
          path: "cat1.jpg"
          lastModified: 1651087606513
          lastModifiedDate: Wed Apr 27 2022 13:26:46 GMT-0600 (Mountain Daylight Time) {}
          name: "cat1.jpg"
          size: 5256
          type: "image/jpeg"
          webkitRelativePath: ""
        }
    */
    const newDzPhoto = getPhotoMetaObject();
    const key = uuidv4();
    newDzPhoto.key = key;
    newDzPhoto.id = key;
    newDzPhoto.SortOrder = maxSort + index + 1;
    newDzPhoto.IsDeleted = false;
    newDzPhoto.dropZoneMeta.file = photo;
    // need to convert to a safer data ref for redux storage.
    if (photo.blobURL) URL.revokeObjectURL(photo.blobURL);
    // URL.revokeObjectURL(photo.blobURL);
    dispatch(addNewClientPhoto(newDzPhoto));
  };

  const addNewFlexRulePhoto = photo => {
    const newFrPhoto = getPhotoMetaObject();
    const key = uuidv4();
    if (!photo.URL) newFrPhoto.IsError = true;
    else newFrPhoto.IsUploaded = true;
    newFrPhoto.key = key;
    newFrPhoto.id = key;
    newFrPhoto.SortOrder = photo.SortOrder;
    newFrPhoto.IsDeleted = photo.IsDeleted;
    newFrPhoto.Description = photo.Description;
    newFrPhoto.flexMeta = { ...photo };
    newFrPhoto.externalURL = photo.URL;
    newFrPhoto.thumbExternalURL = createThumbnailExternalLink(newFrPhoto);
    dispatch(addFlexRulePhoto(newFrPhoto));
  };

  // const createFileFromUrl = async (dataUrl, name) => fetch(dataUrl).then(r => r.blob()).then(blobFile => new File([blobFile], `${name}.png`, { type: blobFile.type }));

  const getPhotoFileFromApi = async externalURL => {
    try {
      const response = await apiService.Photos.getSingleImage(externalURL);
      return URL.createObjectURL(response);
    } catch (er) {
      // eslint-disable-next-line no-console
      console.error('Error: usePhotoManager getPhotoFileFromApi', er);
      return photoPlaceholder;
    }
  };

  const addEditPhotoMeta = (metaSource, cropperBlob) => {
    // const cropMeta = deriveCropMetaFromOriginal(editedPhotoMeta);

    const originMeta = metaSource;
    const cropMeta = getPhotoMetaObject();
    // const originMeta = photoMeta;
    const newKey = uuidv4(); // create new key to hold original photo;
    cropMeta.key = newKey;
    cropMeta.id = newKey;
    cropMeta.SortOrder = originMeta.SortOrder;
    cropMeta.IsDeleted = originMeta.IsDeleted;
    cropMeta.Description = originMeta.Description;
    cropMeta.cropPointer = originMeta.key; // point to original to use on reset

    originMeta.IsDeleted = true;
    const name = originMeta.Description ?? originMeta.key;
    cropMeta.cropperResult.blobURL = cropperBlob;

    dispatch(addEditPhotoResult(cropMeta, name));
    dispatch(softDeletePhoto(originMeta));
    URL.revokeObjectURL(cropMeta.cropperResult.blobURL);
    URL.revokeObjectURL(cropperBlob);
  };

  const getFullImageFromMeta = photoMeta => `${API.URL}Image/GetAwsFile?file=${photoMeta.externalURL}`;

  const getThumbFromMeta = photoMeta => `${API.URL}Image/GetAwsFile?file=${photoMeta.thumbExternalURL}`;

  const markIsDeleted = photoMeta => {
    const deletedPhotoMeta = photoMeta;
    deletedPhotoMeta.IsDeleted = true;
    dispatch(softDeletePhoto(deletedPhotoMeta));
  };

  const getPhotoToEdit = async () => {
    if (editPhotoMeta) {
      editPhotoMeta.originalURL = getFullImageFromMeta(editPhotoMeta);
      editPhotoMeta.cropperSrcURL = await getPhotoFileFromApi(editPhotoMeta.externalURL);
      return editPhotoMeta;
    }
    return null;
  };
  const removeEditPhotoMeta = () => dispatch(removeEditPhoto());

  const dispatchClearPhotoCollection = () => dispatch(clearPhotoCollection());

  return {
    addNewDropzonePhoto,
    addNewFlexRulePhoto,
    addEditPhotoMeta,

    dispatchClearPhotoCollection,
    getPhotoFileFromApi,
    getFullImageFromMeta,
    getThumbFromMeta,
    getPhotoToEdit,
    maxSort,
    removeEditPhotoMeta,
    markIsDeleted,
  };
};

export default usePhotoManager;
